import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { FrontendBlogCategoriesPage, FrontendBlogCategory } from '../../../../core/api/api.models';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { LongformContent } from '../../../../shared/longform-content/longform-content';

const CATEGORIES_PATH = '/blogs/categories';
const DEFAULT_CATEGORIES_TITLE = 'Blog Categories: Coupon & Savings Guides | SavingArena';
const DEFAULT_CATEGORIES_DESCRIPTION =
  'Browse SavingArena blog categories: coupon guides, store deep dives, seasonal deals and shopping strategies, grouped by topic.';

interface DisplayCategory {
  id: string;
  name: string;
  slug: string;
  postCount: number;
  iconHtml: SafeHtml;
}

@Component({
  selector: 'app-blog-categories-page',
  imports: [Breadcrumb, RouterLink, LongformContent],
  templateUrl: './blog-categories-page.html',
  styleUrl: './blog-categories-page.scss',
})
export class BlogCategoriesPage {
  protected readonly crumbs = breadcrumbTrail(
    { label: 'Blog', path: '/blogs' },
    { label: 'Categories' },
  );

  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly seo = inject(SeoService);
  private readonly searchDebounceMs = 300;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly categories = signal<DisplayCategory[]>([]);
  protected readonly searchQuery = signal('');
  protected readonly isSearching = signal(false);

  // Admin-managed content (Settings › Page Settings › Blog Categories). Empty until saved once,
  // in which case the built-in defaults in the template remain in place.
  protected readonly pageH1 = signal('');
  protected readonly pageH2 = signal('');
  protected readonly pageShortDescription = signal('');
  protected readonly longDescriptionHtml = signal('');

  protected readonly visibleCategories = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();

    if (!query) {
      return this.categories();
    }

    return this.categories().filter((category) =>
      `${category.name} ${category.slug}`.toLowerCase().includes(query),
    );
  });

  constructor() {
    this.loadCategories();
    this.loadPageSettings();
  }

  private loadPageSettings(): void {
    this.api
      .getBlogCategoriesPageSettings()
      .pipe(
        catchError(() => of({ success: false, data: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.applyPageSettings(response.data);
        // Applied even without admin settings, so the page always has a title/description/canonical.
        this.applyPageSeo(response.data);
      });
  }

  private applyPageSeo(settings: FrontendBlogCategoriesPage | null | undefined): void {
    const title = (settings?.metaTitle ?? '').trim() || DEFAULT_CATEGORIES_TITLE;
    const description = (settings?.metaDescription ?? '').trim() || DEFAULT_CATEGORIES_DESCRIPTION;
    const url = this.seo.absolute(CATEGORIES_PATH);

    this.seo.apply({ title, description, canonicalPath: CATEGORIES_PATH });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  private applyPageSettings(settings: FrontendBlogCategoriesPage | null | undefined): void {
    if (!settings) {
      return;
    }

    this.pageH1.set((settings.h1 ?? '').trim());
    this.pageH2.set((settings.h2 ?? '').trim());
    this.pageShortDescription.set((settings.shortDescription ?? '').trim());
    this.longDescriptionHtml.set((settings.longDescription ?? '').trim());
  }

  protected onSearchInput(value: string): void {
    if (this.searchTimer !== null) {
      clearTimeout(this.searchTimer);
    }

    this.isSearching.set(true);
    this.searchTimer = setTimeout(() => {
      this.searchQuery.set(value);
      this.isSearching.set(false);
      this.searchTimer = null;
    }, this.searchDebounceMs);
  }

  private loadCategories(): void {
    this.api
      .getBlogCategories()
      .pipe(
        catchError(() => of({ success: false, data: [] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.categories.set(this.toDisplayCategories(response.data));
      });
  }

  private toDisplayCategories(categories: FrontendBlogCategory[]): DisplayCategory[] {
    return categories.map((category) => ({
      id: category.id,
      name: category.name,
      slug: category.slug || this.slugify(category.name),
      postCount: category.totalBlogs ?? this.countFromUnknown(category.blogs),
      iconHtml: this.categoryIconHtml(category),
    }));
  }

  private categoryIconHtml(category: FrontendBlogCategory): SafeHtml {
    if (category.iconCode?.trim()) {
      return this.sanitizer.bypassSecurityTrustHtml(category.iconCode);
    }

    return this.sanitizer.bypassSecurityTrustHtml(
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path stroke-linecap="round" stroke-linejoin="round" d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path stroke-linecap="round" stroke-linejoin="round" d="M8 7h8"/><path stroke-linecap="round" stroke-linejoin="round" d="M8 11h6"/></svg>',
    );
  }

  private countFromUnknown(value: unknown): number {
    if (Array.isArray(value)) {
      return value.length;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    return 0;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
